import { type SubmitEventHandler, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/button'
import { Input } from 'ui/input'
import { LoadingButton } from 'ui/loading-button'
import { Skeleton } from 'ui/skeleton'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import { EmailConfirmation } from '~/boundedContext/auth/pages/email-confirmation'
import { GoogleIcon } from '~/components/google-icon'
import { LinkButton } from '~/components/link-button'
import { OrDelimiter } from '~/components/or-delimiter'
import { config } from '~/config/env'
import { LS_JWT_KEY } from '~/const/api'
import { useSearchParamsError } from '~/hooks/use-search-params-error'
import { Route } from '~/routes/auth/register'
import { useAccountStore } from '~/stores/account'
import { getIsDarkMode } from '~/utils/get-is-dark-mode'
import { clearLastVisitedRoute } from '~/utils/last-visited-route'
import { clearPushSubscriptionForCurrentSession } from '~/utils/push-subscription-session'

import { AuthForm } from '../components/auth-form'

export const Register = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isEmailConfirmScreen, setIsEmailConfirmScreen] = useState(false)
  const { plan: planId } = Route.useSearch()
  const [isTurnstileLoaded, setIsTurnstileLoaded] = useState(() => 'turnstile' in window)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null)

  const {
    t,
    i18n: { language },
  } = useTranslation()

  useSearchParamsError()

  useEffect(() => {
    const turnstileContainer = turnstileContainerRef.current
    if (!isTurnstileLoaded || !turnstileContainer) return

    turnstile.render(turnstileContainer, {
      sitekey: config.VITE_TURNSTILE_SITEKEY,
      theme: getIsDarkMode() ? 'dark' : 'light',
      size: 'flexible',
      callback: setTurnstileToken,
    })
  }, [isTurnstileLoaded])

  if (isEmailConfirmScreen) return <EmailConfirmation />

  const onSubmit: SubmitEventHandler = e => {
    e.preventDefault()
    const form = e.currentTarget as HTMLFormElement
    const email = (form.elements.namedItem('email') as HTMLInputElement).value
    const password = (form.elements.namedItem('password') as HTMLInputElement).value
    const username = (form.elements.namedItem('username') as HTMLInputElement).value
    if (!email || !password) return

    const isTurnstileExpired = turnstile.isExpired(turnstileContainerRef.current ?? undefined)

    if (isTurnstileExpired || !turnstileToken) {
      turnstile.reset(turnstileContainerRef.current ?? undefined)

      toast.error(t('Anti-bot verification expired Please try again'))

      return
    }

    setIsLoading(true)

    apiClient
      .POST('/auth/register', {
        body: { email, password, username, planId, turnstileToken },
        params: {
          header: {
            'x-timezone-offset': (-new Date().getTimezoneOffset() / 60).toString(),
            'x-app-locale': language.split('-')[0] || 'en',
          },
        },
      })
      .then(({ response }) => {
        setIsLoading(false)
        if (response.status === 403) return toast.error(t('The email is already in use'))

        if (response.status >= 400) return toast.error(t('Something went wrong'))

        setIsEmailConfirmScreen(true)
      })
      .catch(error => {
        toast.error(t('Network error'))
        console.error({ message: 'Network error', error })
      })
      .finally(() => setIsLoading(false))
  }

  const onScriptRef = (script: HTMLScriptElement) => {
    const turnstileLoaded = () => setIsTurnstileLoaded(true)

    script.addEventListener('load', turnstileLoaded)

    return () => script.removeEventListener('load', turnstileLoaded)
  }

  return (
    <>
      <AuthForm onSubmit={onSubmit}>
        <Input autoComplete="username" name="username" placeholder={t('Username')} />
        <Input autoComplete="email" name="email" placeholder={t('Email')} type="email" required />
        <Input autoComplete="new-password" name="password" placeholder={t('Password')} type="password" required />
        <div ref={turnstileContainerRef} className="relative h-[65px]">
          <Skeleton className="absolute size-full" />
          {turnstileToken && <div data-testid="turnstile-challenge-completed" />}
        </div>
        <LoadingButton isLoading={isLoading} type="submit">
          {t('Register')}
        </LoadingButton>
        <OrDelimiter />
        <Button type="button">
          <a className="flex gap-2" href={`${import.meta.env.VITE_API_BASE}/auth/google`}>
            <GoogleIcon />
            {t('Register with google')}
          </a>
        </Button>
        <LinkButton
          onClick={() => {
            void clearPushSubscriptionForCurrentSession()
            localStorage.removeItem(LS_JWT_KEY)
            clearLastVisitedRoute()
            useAccountStore.getState().clearAccount()
          }}
          to="/auth/login"
          variant="outline"
        >
          {t('Back to login')}
        </LinkButton>
      </AuthForm>
      <script ref={onScriptRef} src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" async />
    </>
  )
}
